import { Link, useNavigate } from "react-router-dom";
import React, { useRef, useState } from "react";
import CinematicProductLayout, {
  CinematicHero,
  StackingSection,
  CinematicText,
  HorizontalScrollCarousel,
  ImmersiveScrollTrack,
  AmbientParticles,
  SpotlightCard
} from "./shared/CinematicProductLayout";
import ConnectivityContent from "./shared/ConnectivityContent";
import ModulesCarousel from "./shared/ModulesCarousel";
import { Box, Eye, Globe, Cpu, Layers, ArrowRight, Zap, Smartphone, Wifi, Monitor, ShieldCheck, MapPin, Plug } from "lucide-react";
import { motion, useScroll, useTransform, AnimatePresence } from "motion/react";

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: "easeOut" } }
};

const glowPulse = {
  animate: {
    opacity: [0.4, 0.8, 0.4],
    scale: [1, 1.05, 1],
    transition: { duration: 4, repeat: Infinity, ease: "easeInOut" }
  }
};

// Placeholder images - using Intelligence ones for now or simple colors/gradients via CSS if needed
// In a real scenario, we would need specific ARK product shots
const heroImageUrl = process.env.PUBLIC_URL + "/fozzil-intelligence-core.png"; // Placeholder
const genericTechImage = process.env.PUBLIC_URL + "/fozzil-intelligence-image2.png"; // Placeholder

const FozzilArk = () => {
    const navigate = useNavigate();
    // Parallax hook for smoother scroll effects
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: containerRef });
    const yHero = useTransform(scrollYProgress, [0, 1], [0, -100]);

  return (
    <CinematicProductLayout>
      <AmbientParticles />
      
      {/* 1. HERO SECTION */}
      <section ref={containerRef} className="relative z-0">
          <CinematicHero
            bgImage={heroImageUrl}
            bgSize="contain"
            className="-mt-20 md:-mt-32" // Shift image up
            style={{ y: yHero }} // Apply parallax
          >
             <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-16 lg:p-24 pb-12 md:pb-20">
                <div className="flex flex-col md:flex-row items-end justify-between w-full gap-8">
                    {/* Typography - Bottom Left */}
                    <motion.div 
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                        className="text-left max-w-2xl"
                    >
                        <motion.span variants={fadeInUp} className="text-blue-500 font-mono tracking-widest text-sm md:text-base mb-4 block uppercase font-bold">
                            The Evolution of the Exhibit.
                        </motion.span>
                        <motion.h1 variants={fadeInUp} className="text-5xl md:text-8xl lg:text-[7vw] font-bold bg-clip-text text-transparent bg-gradient-to-b from-white via-white to-white/40 tracking-tighter leading-[0.9] mb-6">
                            FOZZIL<br/>ARK
                        </motion.h1>
                        <motion.h3 variants={fadeInUp} className="text-2xl md:text-3xl text-white font-medium mb-6 italic tracking-tight">
                            History, Preserved in Intelligence.
                        </motion.h3>                        
                    </motion.div>

                    {/* CTA - Bottom Right */}
                    <motion.div 
                         initial={{ opacity: 0, x: 20 }}
                         whileInView={{ opacity: 1, x: 0 }}
                         transition={{ delay: 0.5, duration: 0.8 }}
                         className="flex flex-col items-end gap-6 mb-1 md:mb-2"
                    >
                        <Link to="/shop">
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-white border border-white rounded-full py-3 px-6 flex items-center gap-3 hover:bg-blue-900/30 hover:border-blue-500/50 transition-all cursor-pointer group"
                            >
                                <span className="text-black group-hover:text-white font-bold text-base transition-colors">Buy Now</span>
                                <ArrowRight size={18} className="text-black group-hover:text-white group-hover:translate-x-1 transition-all" />
                            </motion.div>
                        </Link>
                    </motion.div>
                </div>
             </div>
          </CinematicHero>
      </section>

      {/* 2. HIGHLIGHTS GRID (Apple "Get the highlights" style) */}
      <section className="relative z-10 bg-black pt-16 pb-20 md:pb-32 px-6">
        <div className="max-w-7xl mx-auto">
            <motion.h2 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-4xl md:text-6xl font-bold text-white mb-16 tracking-tighter"
            >
                Get the highlights.
            </motion.h2>
            
            <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-50px" }}
                variants={staggerContainer}
                className="grid grid-cols-2 md:grid-cols-3 grid-rows-2 gap-3 md:gap-6 h-auto md:h-[800px]"
            >
                {/* Chip / Performance Card - Large Square */}
                {/* Chip / Performance Card - Large Square */}
                <motion.div variants={scaleIn} className="col-span-1 md:col-span-2 row-span-2 bg-zinc-900 border border-blue-500/20 rounded-[24px] md:rounded-[40px] p-4 md:p-14 flex flex-col justify-between overflow-hidden relative group min-h-[400px] md:min-h-0">
                    <div className="relative z-10">
                        <span className="text-zinc-400 font-medium text-xs md:text-2xl mb-2 block leading-tight">Intelligence At your Command!</span>
                        <h3 className="text-xl md:text-6xl font-bold text-white tracking-tighter leading-tight">
                            T.A.E.L.S
                        </h3>
                         <p className="text-zinc-400 text-xs md:text-lg mt-2 md:mt-4 max-w-md hidden md:block">
                            Processes audience data locally and instantly—no cloud lag, total privacy.
                         </p>
                    </div>
                    <motion.div variants={glowPulse} animate="animate" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150px] md:w-[300px] h-[150px] md:h-[300px] bg-blue-900/20 blur-[50px] md:blur-[100px] rounded-full group-hover:bg-blue-800/30 transition-colors duration-700" />
                     <motion.img 
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 0.8, scale: 1 }}
                        src={heroImageUrl} 
                        alt="Neural Chip" 
                        className="absolute inset-0 w-full h-full object-cover object-center opacity-100 group-hover:scale-105" 
                    />
                </motion.div>

                {/* AI / Intelligence Card */}
                <motion.div 
                    variants={scaleIn} 
                    onClick={() => navigate('/stories/ark')}
                    className="bg-gradient-to-br from-zinc-900 to-blue-950/40 border border-blue-500/20 rounded-[24px] md:rounded-[40px] p-3 md:p-10 flex flex-col justify-between relative overflow-hidden group min-h-[190px] md:min-h-0 cursor-pointer"
                >
                    <div className="absolute inset-0 bg-blue-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    <Cpu className="text-white w-8 h-8 md:w-12 md:h-12 mb-2 md:mb-4 relative z-10 shrink-0" />
                    <div className="relative z-10 flex justify-between items-end">
                        <div className="flex-1">
                            <h3 className="text-sm md:text-3xl font-bold text-white mb-1 md:mb-2 leading-tight">The Living Ancestor Engine.</h3>
                            <p className="text-zinc-400 text-[10px] md:text-lg leading-snug line-clamp-3 md:line-clamp-none">Seamlessly host hyper-realistic AI avatars in 100+ languages.</p>
                        </div>
                        <div 
                            className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-white/10 border border-white/20 text-white ml-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:bg-white group-hover:text-black transition-all duration-500 ease-out"
                        >
                            <ArrowRight size={24} />
                        </div>
                    </div>
                </motion.div>

                {/* OS / Security Card (Opens in new tab always) */}
                <motion.div 
                    variants={scaleIn} 
                    onClick={() => window.open('/privacy', '_blank', 'noopener,noreferrer')}
                    className="bg-gradient-to-br from-zinc-900 to-blue-950/40 border border-blue-500/20 rounded-[24px] md:rounded-[40px] p-3 md:p-10 flex flex-col justify-between relative overflow-hidden group min-h-[190px] md:min-h-0 cursor-pointer"
                >
                    <img src="/S.H.A.D.E logo-wobg.png" alt="S.H.A.D.E Vision" className="w-8 h-auto md:w-12 mb-2 md:mb-4 relative z-10 opacity-90 object-contain shrink-0" />
                    <div className="relative z-10 flex justify-between items-end">
                        <div className="flex-1">
                            <h3 className="text-sm md:text-3xl font-bold text-white mb-1 md:mb-2 leading-tight">S.H.A.D.E. Vision.</h3>
                            <p className="text-zinc-400 text-[10px] md:text-lg leading-snug line-clamp-3 md:line-clamp-none">Equipped with spatial awareness, the ARK "sees" your visitors.</p>
                        </div>
                        <div 
                            className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-white/10 border border-white/20 text-white ml-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:bg-white group-hover:text-black transition-all duration-500 ease-out"
                        >
                            <ArrowRight size={24} />
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </div>
      </section>

      {/* 3. PERFORMANCE DEEP DIVE ("Happily ever faster") */}
      <section className="relative z-10 bg-black py-16 md:py-32 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto text-center">
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-100px" }}
                variants={staggerContainer}
            >
                <motion.span variants={fadeInUp} className="text-zinc-500 font-medium text-base md:text-2xl mb-4 md:mb-6 block tracking-wide">Intelligence with a Soul</motion.span>
                <motion.h2 variants={fadeInUp} className="text-3xl md:text-[8vw] font-bold text-white tracking-tighter leading-[0.9] mb-8">
                    The Black Box. <br/>
                    <span className="text-blue-500">Reimagined.</span>
                </motion.h2>
                
                <div className="relative w-full max-w-lg mx-auto aspect-square flex items-center justify-center my-16">
                     {/* Glowing Chip Visualization */}
                     <motion.div 
                        animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.1, 1] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute inset-0 bg-blue-600/20 blur-[100px] rounded-full" 
                     />
                     <motion.div 
                        initial={{ scale: 0.8, opacity: 0, rotate: -10 }}
                        whileInView={{ scale: 1, opacity: 1, rotate: 0 }}
                        transition={{ duration: 1, type: "spring" }}
                        className="relative z-10 w-40 h-40 md:w-64 md:h-64 border border-white/20 bg-black/80 backdrop-blur-md rounded-[32px] flex items-center justify-center shadow-[0_0_50px_rgba(37,99,235,0.3)]"
                     >
                        <img src="/logoicon.png" alt="FOZZIL AI Core" className="w-20 h-20 md:w-40 md:h-40 object-contain opacity-90" />
                     </motion.div>
                </div>
                
                <motion.p variants={fadeInUp} className="text-base md:text-3xl text-zinc-400 font-light leading-relaxed max-w-4xl mx-auto">
                    At the heart of ARK is the <span className="text-white font-medium">T.A.E.L.S Core</span>. Designed specifically for low-latency spatial reasoning, it processes over 18 trillion operations per second. It allows the Living Ancestor Engine to render hyper-realistic avatars that don't just follow a script—they respond to the flicker of a visitor's emotion.
                </motion.p>
            </motion.div>
        </div>
      </section>

      {/* 4. INTELLIGENCE ("Built for AI") */}
      <section className="relative z-10 bg-zinc-950 py-16 md:py-32 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
            <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-100px" }}
                variants={staggerContainer}
                className="text-left mb-12 md:mb-24"
            >
                <motion.span variants={fadeInUp} className="text-blue-500 font-medium text-xl mb-4 block">S.H.A.D.E. Awareness</motion.span>
                <motion.h2 variants={fadeInUp} className="text-3xl md:text-8xl font-bold text-white tracking-tighter leading-none mb-6 md:mb-8">
                    An exhibit that <br/>
                    knows you’re there.
                </motion.h2>
                <motion.p variants={fadeInUp} className="text-base md:text-2xl text-zinc-400 max-w-3xl leading-relaxed">
                    ARK doesn’t just project; it perceives. Using the S.H.A.D.E. (Secure Hostile Adaptive Defence Engine), it maps the room in real-time. It recognizes group dynamics, detects the curiosity in a child’s face, and adjusts the narrative depth instantly. It’s the first time history has ever looked back at you.
                </motion.p>
            </motion.div>

            <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-100px" }}
                variants={staggerContainer}
                className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-8"
            >
                <motion.div variants={scaleIn} className="bg-gradient-to-br from-zinc-900 to-blue-950/40 border border-blue-500/20 rounded-[20px] md:rounded-[40px] p-3 md:p-12 min-h-[180px] md:min-h-[500px] flex flex-col justify-end relative overflow-hidden group">
                     {/* "Born to run" style card */}
                     <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-blue-900/10 to-transparent" />
                     <h3 className="text-sm md:text-3xl font-bold text-white mb-1 md:mb-4 relative z-10">Hyper-realistic avatars.</h3>
                     <p className="text-zinc-400 text-[10px] md:text-lg relative z-10 leading-tight">
                        The Living Ancestor Engine allows history to speak in 100+ languages with zero latency.
                     </p>
                </motion.div>
                <motion.div variants={scaleIn} className="bg-gradient-to-br from-zinc-900 to-blue-950/40 border border-blue-500/20 rounded-[20px] md:rounded-[40px] p-3 md:p-12 min-h-[180px] md:min-h-[500px] flex flex-col justify-end relative overflow-hidden group">
                     <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-purple-900/10 to-transparent" />
                     <h3 className="text-sm md:text-3xl font-bold text-white mb-1 md:mb-4 relative z-10">Universal Projection Link.</h3>
                     <p className="text-zinc-400 text-[10px] md:text-lg relative z-10 leading-tight">
                        A single-cable output (HDMI 2.1) transforms any wall into a 8K FOZZIL experience.
                     </p>
                </motion.div>
            </motion.div>
        </div>
      </section>

      {/* 4.5. DISPLAY / OUTPUT ("Let there be delight") */}
      <section className="relative z-10 bg-black py-16 md:py-32 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto mb-12 md:mb-24">
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-100px" }}
                variants={staggerContainer}
            >
                <motion.h2 variants={fadeInUp} className="text-3xl md:text-7xl font-bold text-white mb-6 md:mb-8 tracking-tighter">Universal Projection Link.</motion.h2>
                <motion.p variants={fadeInUp} className="text-base md:text-3xl text-zinc-400 font-light leading-relaxed max-w-4xl">
                    A single-cable output (HDMI 2.1) that transforms any wall, OLED screen, or transparent glass into a 8K FOZZIL experience.
                </motion.p>
            </motion.div>
        </div>
        {/* Visuals for Display - mimicking the "XDR" liquid screen */}
        <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 50 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-7xl mx-auto h-[250px] md:h-[600px] bg-zinc-900 rounded-[24px] md:rounded-[40px] overflow-hidden relative flex items-center justify-center border border-white/10 group"
        >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-black to-purple-900/20 opacity-60 group-hover:opacity-100 transition-opacity duration-1000" />
            
            {/* Abstract Screen Content */}
            <div className="relative z-10 text-center scale-90 md:scale-100 transition-transform duration-1000 group-hover:scale-105">
                 <div className="w-[80vw] md:w-[60vw] h-[40vh] md:h-[50vh] bg-black rounded-lg shadow-2xl overflow-hidden border border-white/5 relative">
                     {/* Simulating vibrant content */}
                     <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(59,130,246,0.5),_transparent_70%)] animate-pulse" />
                     <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                        <h3 className="text-4xl md:text-9xl font-bold text-white tracking-tighter drop-shadow-[0_0_30px_rgba(59,130,246,0.8)]">8K</h3>
                        <p className="text-blue-200 text-xl tracking-[0.5em] uppercase mt-4 font-bold">Ultra-HD Output</p>
                     </div>
                 </div>
            </div>
        </motion.div>
        <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ margin: "-50px" }}
            variants={staggerContainer}
            className="max-w-7xl mx-auto mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left"
        >
            <motion.div variants={fadeInUp}>
                <h4 className="text-white font-bold text-xl mb-2">8K Resolution</h4>
                <p className="text-zinc-500">Up to 8K Ultra-HD at 60fps for crystal clear visual experiences.</p>
            </motion.div>
            <motion.div variants={fadeInUp}>
                <h4 className="text-white font-bold text-xl mb-2">HDMI 2.1</h4>
                <p className="text-zinc-500">High-bandwidth connection for the modern display ecosystem.</p>
            </motion.div>
            <motion.div variants={fadeInUp}>
                <h4 className="text-white font-bold text-xl mb-2">Multi-Canvas Support</h4>
                <p className="text-zinc-500">Connect multiple units to create a synchronized 360° panoramic experience.</p>
            </motion.div>
        </motion.div>
      </section>

      {/* 4.6. OS ("Fresh faced. Timelessly Mac" -> "FOZZIL Core. Pure Control.") */}
      <section className="relative z-10 bg-zinc-950 py-16 md:py-32 px-6">
         <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-16">
             <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-100px" }}
                variants={staggerContainer}
                className="flex-1"
             >
                 <motion.span variants={fadeInUp} className="text-blue-500 font-medium text-xl mb-4 block">Ecosystem</motion.span>
                 <motion.h2 variants={fadeInUp} className="text-3xl md:text-6xl font-bold text-white tracking-tighter leading-tight mb-8">
                     It plays well <br/> with others.
                 </motion.h2>
                 <motion.p variants={fadeInUp} className="text-base md:text-xl text-zinc-400 leading-relaxed mb-8">
                     ARK is the anchor of the FOZZIL Ecosystem. Sync your museum’s data via FOZZIL ID. Manage transactions with FOZZIL Pay. One device, one identity, an infinite world of culture.
                 </motion.p>
                 <motion.div variants={fadeInUp}>
                     <a href={process.env.REACT_APP_ECOSYSTEM_URL || "https://ecosystem.fozzil.in"} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-lg font-medium hover:underline flex items-center gap-2 group">
                         Learn more about FOZZIL Core <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform"/>
                     </a>
                 </motion.div>
             </motion.div>
             <motion.div 
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="flex-1 w-full h-[300px] md:h-[500px] bg-zinc-900 rounded-[30px] border border-white/10 relative overflow-hidden group shadow-2xl"
             >
                 {/* Abstract Dashboard UI */}
                 <div className="absolute inset-0 bg-zinc-950/80" />
                 <div className="absolute top-12 left-12 right-12 bottom-0 bg-zinc-900 rounded-t-2xl border-t border-l border-r border-white/10 shadow-2xl p-6 transform group-hover:-translate-y-4 transition-transform duration-700">
                     <div className="flex items-center gap-2 mb-8 border-b border-white/5 pb-4">
                         <div className="w-3 h-3 rounded-full bg-red-500/80" />
                         <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                         <div className="w-3 h-3 rounded-full bg-green-500/80" />
                         <div className="ml-4 h-4 w-32 bg-white/10 rounded-full" />
                     </div>
                     <div className="space-y-4">
                         <div className="grid grid-cols-3 gap-4">
                             <div className="h-32 bg-blue-900/20 border border-blue-500/20 rounded-xl" />
                             <div className="h-32 bg-white/5 rounded-xl col-span-2" />
                         </div>
                         <div className="h-40 bg-white/5 rounded-xl" />
                     </div>
                 </div>
             </motion.div>
         </div>
      </section>

      {/* 4.7. APPS ("There's an app for that") */}
      <section className="relative z-10 bg-black py-16 md:py-40 px-6 border-b border-white/5">
         <div className="max-w-7xl mx-auto text-center mb-24">
             <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-100px" }}
                variants={staggerContainer}
             >
                 <motion.h2 variants={fadeInUp} className="text-3xl md:text-7xl font-bold text-white mb-8 tracking-tighter">
                     Your ambitions. <br/> There’s a module for that.
                 </motion.h2>
                 <motion.p variants={fadeInUp} className="text-base md:text-xl text-zinc-400 max-w-3xl mx-auto leading-normal">
                     Tens of thousands of AI models and visitor experiences are optimized for FOZZIL silicon — from your go-to analytics tools to hyper-realistic avatars. With FOZZIL ARK, they all soar.
                 </motion.p>
             </motion.div>
         </div>
         
         <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ margin: "-50px" }}
            variants={staggerContainer}
            className="max-w-7xl mx-auto mt-16"
         >
             <ModulesCarousel />
         </motion.div>
      </section>
      <section className="relative z-10 bg-black py-16 md:py-48 px-6">
        <div className="max-w-7xl mx-auto">
            <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-100px" }}
                variants={staggerContainer}
                className="mb-24 max-w-3xl"
            >
                <motion.span variants={fadeInUp} className="text-zinc-500 font-medium text-xl mb-4 block">Ports and Connectivity</motion.span>
                <motion.h2 variants={fadeInUp} className="text-3xl md:text-7xl font-bold text-white tracking-tighter leading-tight mb-8">
                    Make powerful <br/>
                    connections.
                </motion.h2>
                <motion.p variants={fadeInUp} className="text-base md:text-xl text-zinc-400 leading-relaxed max-w-2xl">
                     FOZZIL ARK packs an array of ports for connecting high-speed peripherals, driving high-resolution displays, or directly offloading data. Featuring Thunderbolt-speed USB-C architecture and HDMI 2.1 for 8K output.
                </motion.p>
            </motion.div>

            {/* Connectivity Section Body */}
            {/* Connectivity Section Body */}
            <ConnectivityContent />
        </div>
      </section>

      {/* 5.5. HOW IT WORKS */}
      <section className="relative z-10 bg-black py-16 md:py-48 px-6 border-t border-white/5">
         <div className="max-w-7xl mx-auto">
             <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-100px" }}
                variants={staggerContainer}
                className="text-center mb-24"
             >
                <motion.h2 variants={fadeInUp} className="text-3xl md:text-7xl font-bold text-white tracking-tighter mb-6">
                    Simple. <br/>
                    Seamless. <br/>
                    Secure.
                </motion.h2>
                <motion.p variants={fadeInUp} className="text-base md:text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed">
                    Set up your immersive space in minutes. No complex rigging required.
                </motion.p>
             </motion.div>

             <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-50px" }}
                variants={staggerContainer}
                className="grid grid-cols-3 md:grid-cols-3 gap-2 md:gap-12"
             >
                 <motion.div variants={fadeInUp}>
                    <StepCard 
                        number="1" 
                        title="Place" 
                        desc="Position the ARK near your exhibit area." 
                        icon={<MapPin className="text-white w-5 h-5 md:w-10 md:h-10" />} 
                    />
                 </motion.div>
                 <motion.div variants={fadeInUp}>
                    <StepCard 
                        number="2" 
                        title="Plug" 
                        desc="Connect to power and your display (Projector/TV)." 
                        icon={<Plug className="text-white w-5 h-5 md:w-10 md:h-10" />} 
                    />
                 </motion.div>
                 <motion.div variants={fadeInUp}>
                    <StepCard 
                        number="3" 
                        title="Perform" 
                        desc="Use the FOZZIL App to select your Living History module and watch the space come alive." 
                        icon={<Smartphone className="text-white w-5 h-5 md:w-10 md:h-10" />} 
                    />
                 </motion.div>
             </motion.div>
         </div>
      </section>

      {/* 6. SECURITY ("No compromises") */}
      <section className="relative z-10 bg-zinc-950 py-16 md:py-48 px-6">
        <div className="max-w-7xl mx-auto">
            <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-100px" }}
                variants={staggerContainer}
                className="text-center mb-24"
            >
                <motion.span variants={fadeInUp} className="text-zinc-500 font-medium text-xl mb-4 block">Security</motion.span>
                <motion.h2 variants={fadeInUp} className="text-3xl md:text-8xl font-bold text-white tracking-tighter mb-8">
                    No compromises.
                </motion.h2>
                <motion.div variants={scaleIn} className="relative w-full max-w-4xl mx-auto aspect-video bg-zinc-900 rounded-[30px] overflow-hidden border border-white/5 shadow-2xl flex items-center justify-center mb-16">
                     {/* Placeholder for Security Interface UI */}
                     <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/10 to-transparent" />
                     <div className="text-center z-10 p-8">
                          <img src="/S.H.A.D.E logo-wobg.png" alt="Secure Enclave" className="w-16 h-auto md:w-24 mx-auto mb-6 opacity-90 object-contain" />
                          <h3 className="text-3xl font-bold text-white mb-2">FOZZIL Secure Enclave</h3>
                          <p className="text-zinc-400">System Integrity Verified</p>
                     </div>
                </motion.div>
                <motion.p variants={fadeInUp} className="text-base md:text-2xl text-zinc-400 max-w-4xl mx-auto leading-relaxed">
                    FOZZIL Privacy, FOZZIL ID, and the Ecosystem work in unison to protect your digital life. From on-device processing to secure identity authentication, every interaction is designed to be safe, private, and seamlessly connected.
                </motion.p>
            </motion.div>

            <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-50px" }}
                variants={staggerContainer}
                className="grid grid-cols-3 md:grid-cols-3 gap-2 md:gap-12"
            >
                 <motion.div variants={fadeInUp} className="flex flex-col items-center text-center">
                    <div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-blue-500/10 flex items-center justify-center mb-3 md:mb-6 text-blue-500">
                        <svg className="w-5 h-5 md:w-8 md:h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                    </div>
                    <h3 className="text-[10px] md:text-xl font-bold text-white mb-1 md:mb-2">Secure Boot</h3>
                    <p className="text-zinc-500 text-[8px] md:text-base leading-tight md:leading-normal">Hardware-verified boot process ensures system integrity.</p>
                 </motion.div>
                 <motion.div variants={fadeInUp} className="flex flex-col items-center text-center">
                    <div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-blue-500/10 flex items-center justify-center mb-3 md:mb-6 text-blue-500">
                        <svg className="w-5 h-5 md:w-8 md:h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    </div>
                    <h3 className="text-[10px] md:text-xl font-bold text-white mb-1 md:mb-2">Asset Tracking</h3>
                    <p className="text-zinc-500 text-[8px] md:text-base leading-tight md:leading-normal">Locate and remotely lock misplaced ARK units.</p>
                 </motion.div>
                 <motion.div variants={fadeInUp} className="flex flex-col items-center text-center">
                    <div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-blue-500/10 flex items-center justify-center mb-3 md:mb-6 text-blue-500">
                        <svg className="w-5 h-5 md:w-8 md:h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </div>
                    <h3 className="text-[10px] md:text-xl font-bold text-white mb-1 md:mb-2">Neural Vault</h3>
                    <p className="text-zinc-500 text-[8px] md:text-base leading-tight md:leading-normal">Encrypt and protect sensitive visitor data via on-chip keys.</p>
                 </motion.div>
            </motion.div>
        </div>
      </section>

      {/* 7. VALUES ("Our values lead the way") */}
      <section className="relative z-10 bg-zinc-950 py-16 md:py-32 px-6 overflow-hidden">
         <div className="max-w-7xl mx-auto">
             <motion.h2 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-3xl md:text-7xl font-bold text-white mb-12 md:mb-24 tracking-tighter text-center"
             >
                Our values lead the way.
             </motion.h2>
             
             <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-50px" }}
                variants={staggerContainer}
                className="grid grid-cols-3 md:grid-cols-3 gap-2 md:gap-8"
             >
                 {/* Card 1 */}
                 <motion.div variants={scaleIn} className="bg-black border border-white/10 rounded-[20px] md:rounded-[40px] p-3 md:p-10 shadow-xl flex flex-col justify-between min-h-[220px] md:h-[400px] overflow-hidden group">
                     <div>
                         <Layers className="w-6 h-6 md:w-12 md:h-12 text-blue-500 mb-3 md:mb-6" />
                         <h3 className="text-[10px] md:text-4xl font-bold text-white leading-tight">
                             Enclosure: <br/>
                             <span className="text-blue-500">100% Recycled</span> <br/>
                             Aluminum.
                         </h3>
                         <p className="text-zinc-500 mt-2 font-medium text-[8px] md:text-base">Cosmic Black Finish</p>
                     </div>
                     <div className="w-6 h-6 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center text-black ml-auto group-hover:scale-110 transition-transform">
                        <ArrowRight size={10} className="md:w-5 md:h-5" />
                     </div>
                 </motion.div>
                 
                 {/* Card 2 */}
                 <motion.div variants={scaleIn} className="bg-black border border-white/10 rounded-[20px] md:rounded-[40px] p-3 md:p-10 shadow-xl flex flex-col justify-between min-h-[220px] md:h-[400px] overflow-hidden group">
                     <div>
                         <Zap className="w-6 h-6 md:w-12 md:h-12 text-blue-400 mb-3 md:mb-6" />
                         <h3 className="text-[10px] md:text-4xl font-bold text-white leading-tight">
                             Internal: <br/>
                             <span className="text-blue-400">98% Rare Earth</span> <br/>
                             Recycled.
                         </h3>
                     </div>
                     <div className="w-6 h-6 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center text-black ml-auto group-hover:scale-110 transition-transform">
                        <ArrowRight size={10} className="md:w-5 md:h-5" />
                     </div>
                 </motion.div>

                 {/* Card 3 */}
                 <motion.div variants={scaleIn} className="bg-black border border-white/10 rounded-[20px] md:rounded-[40px] p-3 md:p-10 shadow-xl flex flex-col justify-between min-h-[220px] md:h-[400px] overflow-hidden group">
                     <div>
                         <Box className="w-6 h-6 md:w-12 md:h-12 text-cyan-500 mb-3 md:mb-6" />
                         <h3 className="text-[10px] md:text-4xl font-bold text-white leading-tight">
                             Lattice <br/>
                             <span className="text-cyan-500">Venting</span> <br/>
                             dynamics.
                         </h3>
                         <p className="text-zinc-500 mt-2 font-medium text-[8px] md:text-base leading-tight">Cool while history stays hot.</p>
                     </div>
                     <div className="w-6 h-6 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center text-black ml-auto group-hover:scale-110 transition-transform">
                        <ArrowRight size={10} className="md:w-5 md:h-5" />
                     </div>
                 </motion.div>
             </motion.div>
             
             {/* Privacy Card - Full Width */}
             <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ margin: "-50px" }}
                variants={scaleIn}
                className="mt-6 bg-black border border-white/10 rounded-[30px] md:rounded-[40px] px-6 py-10 md:px-12 md:py-16 shadow-xl flex flex-col items-center justify-center text-center overflow-hidden group gap-6 md:gap-8"
             >
                 <div className="flex flex-col items-center max-w-4xl">
                     <div className="flex items-center justify-center gap-4 mb-6 md:mb-8">
                        <img src="/S.H.A.D.E logo-wobg.png" alt="S.H.A.D.E Privacy" className="h-16 md:h-32 opacity-90" />
                     </div>
                     <h3 className="text-3xl md:text-7xl font-bold text-white leading-tight mb-4 md:mb-6 tracking-tighter">
                         FOZZIL Privacy. <br/>
                         S.H.A.D.E.
                     </h3>
                     <p className="text-base md:text-2xl text-zinc-400 font-medium leading-relaxed mb-6 md:mb-8 max-w-2xl mx-auto">
                         Privacy is a fundamental human right. Every product and service is designed to help keep your data safe and secure.
                     </p>
                      <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-500 text-base md:text-xl font-medium hover:underline flex items-center justify-center gap-2 group-hover:gap-4 transition-all">
                         Learn more about privacy <ArrowRight size={18} className="md:w-5 md:h-5" />
                     </Link>
                 </div>
             </motion.div>
         </div>
      </section>

      {/* 7. CALL TO ACTION / FOOTER */}
      <section className="relative z-50">
          <ArkConclusionSection />
      </section>

    </CinematicProductLayout>
  );
};

const StepCard = ({ number, title, desc, icon }) => (
    <div className="flex flex-col items-center text-center group relative">
        <div className="w-12 h-12 md:w-24 md:h-24 rounded-[32px] md:rounded-[32px] bg-white/5 border border-white/10 flex items-center justify-center mb-4 md:mb-8 group-hover:bg-blue-600/10 group-hover:border-blue-500/30 group-hover:scale-110 transition-all duration-700 shadow-2xl">
            {icon}
        </div>
        <div className="text-blue-500/40 font-mono text-[8px] md:text-sm mb-1 md:mb-3 tracking-widest uppercase font-bold">Part {number}</div>
        <h3 className="text-xs md:text-3xl font-bold text-white mb-2 md:mb-6 tracking-tight">{title}</h3>
        <p className="text-zinc-400 text-[8px] md:text-lg font-light leading-snug md:leading-relaxed max-w-xs group-hover:text-zinc-200 transition-colors px-1 md:px-0">{desc}</p>
    </div>
);

const ArkConclusionSection = () => {
    return (
        <div className="h-screen relative bg-black flex items-center justify-center px-6 overflow-hidden">
             {/* Final Section Glow */}
             <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[100vw] h-[40vh] bg-blue-600/20 blur-[180px] rounded-full" />
             
             <div className="relative z-10 max-w-5xl w-full text-center">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="space-y-12"
                >
                    <h2 className="text-4xl md:text-[12vw] font-bold text-white tracking-tighter leading-none mb-12">
                        FOZZIL ARK
                    </h2>
                    <p className="text-base md:text-3xl text-zinc-500 mb-16 font-light max-w-3xl mx-auto leading-relaxed">
                        Redefining cultural heritage through spatial intelligence. <br className="hidden md:block"/>
                        Available for Global Lease and B2B Licensing.
                    </p>
                    
                    <Link 
                        to="/shop"
                        className="group relative inline-flex h-16 md:h-20 items-center justify-center overflow-hidden rounded-full bg-white text-black px-8 md:px-12 font-bold text-lg md:text-xl transition-all duration-500 hover:scale-105 active:scale-95 shadow-[0_0_60px_rgba(255,255,255,0.2)]"
                    >
                        <span className="mr-4">Buy FOZZIL ARK</span>
                        <ArrowRight className="h-6 w-6 transition-transform group-hover:translate-x-3" />
                    </Link>
                </motion.div>
             </div>
        </div>
    );
};

export default FozzilArk;
